import { useCallback, useMemo } from 'react';

import { formatBrandVoiceChildRoute } from 'src/lib/routing';
import { ROUTES } from 'src/routes';
import { useBrandVoiceIdsForBrandTerms, useBrandVoicesAssets, useCurrentProject } from 'src/store';
import { useBrandVoices } from '../useBrandVoices';
import { useHistoryPushOrInfinityOpen } from '../useHistoryPushOrInfinityOpen';
import { useSetupProjectVocabulary } from '../useSetupProjectVocabulary';

export const useBrandTermsProps = ({
  detectedBrandTermsData,
  onApplyDetectedBrandTerm,
  onDetectedBrandTermHover,
  onDetectedBrandTermClick,
  disabled,
}) => {
  const currentProject = useCurrentProject();
  const { brandVoices: availableBrandVoices, defaultBrandVoice } = useBrandVoices();
  const { brandTerms: availableBrandTerms } = useBrandVoicesAssets();
  const { brandVoiceIdsForBrandTerms, brandRulesEnabled } = useBrandVoiceIdsForBrandTerms();
  const historyPushOrInfinityOpen = useHistoryPushOrInfinityOpen();
  const setupProjectVocabulary = useSetupProjectVocabulary();

  const availableBrandTermsCount = availableBrandTerms?.length || 0;
  const projectType = currentProject?.type;

  const activeBrandVoices = useMemo(
    () =>
      brandRulesEnabled
        ? availableBrandVoices?.filter((brandVoice) =>
            brandVoiceIdsForBrandTerms?.includes(brandVoice.id)
          )
        : [],
    [availableBrandVoices, brandRulesEnabled, brandVoiceIdsForBrandTerms]
  );

  const handleEmptyTermsClick = useCallback(() => {
    if (availableBrandTermsCount > 0) {
      setupProjectVocabulary();
    } else {
      const route = formatBrandVoiceChildRoute(defaultBrandVoice.id, ROUTES.BRAND_TERMS);
      historyPushOrInfinityOpen(route);
    }
  }, [
    availableBrandTermsCount,
    defaultBrandVoice,
    historyPushOrInfinityOpen,
    setupProjectVocabulary,
  ]);

  return useMemo(
    () => ({
      projectType,
      availableBrandTermsCount,
      activeBrandVoices,
      detectedBrandTerms: detectedBrandTermsData?.items,
      onApplyBrandTerm: onApplyDetectedBrandTerm,
      onBrandTermHover: onDetectedBrandTermHover,
      onBrandTermClick: onDetectedBrandTermClick,
      onSettingsClick: setupProjectVocabulary,
      onEmptyTermsClick: handleEmptyTermsClick,
      loading: detectedBrandTermsData?.loading,
      disabled,
    }),
    [
      projectType,
      availableBrandTermsCount,
      activeBrandVoices,
      detectedBrandTermsData?.items,
      detectedBrandTermsData?.loading,
      onApplyDetectedBrandTerm,
      onDetectedBrandTermHover,
      onDetectedBrandTermClick,
      setupProjectVocabulary,
      handleEmptyTermsClick,
      disabled,
    ]
  );
};
